/**
 * The event half of the command → event contract (architecture §5.3).
 *
 * The event log is the canon: dice, HP, positions, prices, relationships, quests, the
 * board and the spoken words are projections of it and of nothing else. Every
 * projection in the product — HUD, board diffs, dice log, journal, captions,
 * `tool_result`, Batch consolidation — is derived from this union.
 */
import { z } from 'zod';
import { AdvantageState, Cell, CheckKey, ConditionId, SpellAffliction, Cover, DamageType, Lighting, NpcMood, RelationshipAxis, RollKind, RollMode, RollPart, RumorCategory, TimeOfDay, ToneTag, Weather, } from './primitives.js';
import { CombatOutcome, ResolvedSceneSpec, PropState } from './scene-spec.js';
import { NegotiationSlot } from './commands.js';
/* ------------------------------------------------------------- envelope */
const eventEnvelope = z.object({
    /** Per-session monotone; the watermark unit and the dice keying unit. */
    seq: z.number().int().nonnegative(),
    /** The id of the command that produced this event. */
    causedBy: z.string(),
    actorId: z.string().optional(),
    worldMinutes: z.number().int(),
    /** Ledger importance for sifting: combat 8, purchase 2, death 10 (§4.6). */
    importance: z.number().int().min(0).max(10),
});
function defineEvent(kind, payload) {
    return eventEnvelope.extend({ kind: z.literal(kind), payload });
}
/* -------------------------------------------------------- roll payloads */
export const RollTarget = z.object({
    kind: z.enum(['dc', 'ac']),
    value: z.number().int(),
    /** "CDs abertas" is off by default: the tray reads "vs CD ?" until the die lands. */
    shown: z.boolean(),
});
export const RollRequestedPayload = z.object({
    rollId: z.string(),
    actorId: z.string(),
    kind: RollKind,
    /** "d20+5", "1d6+3" */
    expr: z.string(),
    parts: z.array(RollPart),
    target: RollTarget.optional(),
    mode: RollMode,
    advantage: AdvantageState,
    /** Spoken idle prompt after this many ms (default 15 000). */
    promptAfterMs: z.number().int().min(0),
    /** Engine auto-rolls after this many ms; 0 means never (§13.5). */
    autoAfterMs: z.number().int().min(0),
    /** Player-facing breakdown line: "Furtividade: d20 + 5 vs CD 15". */
    breakdown: z.string(),
});
export const RolledDie = z.object({
    sides: z.number().int().min(2),
    value: z.number().int().min(1),
    kept: z.boolean().default(true),
});
export const RollResolvedPayload = z.object({
    rollId: z.string(),
    dice: z.array(RolledDie).min(1),
    total: z.number().int(),
    natural: z.union([z.literal(1), z.literal(20)]).optional(),
    entropySource: z.enum(['engine', 'player']),
    /** "" for engine rolls; sha256 of the throw gesture for I-rolls (§5.4). */
    entropyHash: z.string(),
    autoAfterTimeout: z.boolean().optional(),
    /** Set when a Heroic Inspiration reroll supersedes this roll (§5.4). */
    replacedBy: z.string().optional(),
});
/* --------------------------------------------- combat resolution payloads */
export const AttackResolvedPayload = z.object({
    attackId: z.string(),
    attacker: z.string(),
    target: z.string(),
    total: z.number().int(),
    vsAc: z.number().int(),
    hit: z.boolean(),
    crit: z.boolean(),
    /** ADR-092: the rogue's Sneak Attack dice added to this hit, when the roll had advantage. */
    sneakAttack: z.string().optional(),
    /** ADR-092: the attacker was hidden; the blow gave them away. */
    fromHiding: z.boolean().optional(),
    /** ADR-094: the target's Shield reaction turned the hit into a miss. */
    shielded: z.boolean().optional(),
    cover: Cover,
});
export const DamageAppliedPayload = z.object({
    target: z.string(),
    amount: z.number().int().min(0),
    type: DamageType,
    parts: z.array(RollPart).default([]),
    hpBefore: z.number().int(),
    hpAfter: z.number().int(),
    tempAbsorbed: z.number().int().min(0).default(0),
    bloodied: z.boolean(),
});
export const HealedPayload = z.object({
    /** SRD short rest: Hit Dice spent for this healing, and what is left in the pool. */
    hitDiceSpent: z.number().int().min(0).optional(),
    hitDiceLeft: z.number().int().min(0).optional(),
    target: z.string(),
    amount: z.number().int().min(0),
    hpAfter: z.number().int(),
    /** ADR-092: the feature that healed (`second_wind`), and the uses it has left. */
    feature: z.string().optional(),
    /** ADR-130: the SRD's waking — a character stable at 0 HP regains 1 HP after this many hours (1d4). */
    afterHours: z.number().int().min(1).max(4).optional(),
    usesLeft: z.number().int().min(0).optional(),
});
export const SaveResolvedPayload = z.object({
    actor: z.string(),
    ability: z.enum(['str', 'dex', 'con', 'int', 'wis', 'cha']),
    total: z.number().int(),
    dc: z.number().int(),
    success: z.boolean(),
});
export const CheckResolvedPayload = z.object({
    actor: z.string(),
    key: CheckKey,
    total: z.number().int(),
    dc: z.number().int(),
    success: z.boolean(),
    margin: z.number().int(),
    /** Set when a tone tag modified this check; the HUD chip and Ledger row use it (§3.7). */
    toneApplied: z
        .object({
        tag: ToneTag,
        ruleId: z.string(),
        effect: z.string(),
        rmsRelDb: z.number(),
        confidence: z.number().min(0).max(1),
    })
        .optional(),
});
export const ConcentrationCheckedPayload = z.object({
    caster: z.string(),
    dc: z.number().int(),
    success: z.boolean(),
});
export const SpellCastPayload = z.object({
    caster: z.string(),
    spellId: z.string(),
    slotLevel: z.number().int().min(0).max(9),
    targets: z.array(z.string()).default([]),
    /** ADR-093: how the engine read the spell — attack, save, heal, auto, buff, restore, stabilize, utility. */
    effect: z.string().optional(),
    /** ADR-093: the spell's name, for captions. */
    name: z.string().optional(),
    area: z
        .object({ shape: z.enum(['circle', 'cone', 'line', 'cube']), sizeFt: z.number().int() })
        .optional(),
});
/** ADR-093: a lasting spell ended — its duration ran out, concentration broke, or the fight did. */
export const SpellEndedPayload = z.object({
    caster: z.string(),
    spellId: z.string(),
    /** ADR-097: `collapsed` — webs with nothing to hold them fell in on themselves. */
    reason: z.enum(['expired', 'concentration', 'combat_over', 'replaced', 'attack', 'collapsed']),
});
export const ConditionChangedPayload = z.object({
    target: z.string(),
    condition: z.union([ConditionId, SpellAffliction]),
    source: z.string(),
    /** ADR-096 (Suggestion): the course of activity the charmed creature pursues, in the caster's words. */
    course: z.string().max(200).optional(),
});
/** ADR-094: a condition a spell put on a creature has ended — its time ran out, a save was made, damage woke it, the spell ended. */
export const ConditionEndedPayload = z.object({
    target: z.string(),
    condition: z.union([ConditionId, SpellAffliction]),
    /** ADR-095: `escaped` — the creature tore free (Web's Strength (Athletics) check). */
    reason: z.enum(['expired', 'saved', 'damage', 'spell_ended', 'stood_up', 'combat_over', 'escaped', 'completed', 'burned']),
});
export const EffectStartedPayload = z.object({
    effectId: z.string(),
    name: z.string(),
    source: z.string(),
    owner: z.string(),
    targets: z.array(z.string()).default([]),
    stackingKey: z.string(),
    concentration: z.string().optional(),
    area: z
        .object({
        shape: z.enum(['circle', 'cone', 'line', 'cube']),
        sizeFt: z.number().int(),
        anchorCell: Cell.optional(),
        anchorToken: z.string().optional(),
    })
        .optional(),
});
export const EffectEndedPayload = z.object({
    effectId: z.string(),
    reason: z.enum(['expired', 'dispelled', 'concentration_lost', 'source_died', 'replaced']),
});
export const MovedPayload = z.object({
    actor: z.string(),
    path: z.array(Cell).min(1),
    provoked: z.array(z.string()).default([]),
});
/** SRD Dash: the action spent on a second move, toward a target when there is one (§6.4). */
export const DashedPayload = z.object({ actor: z.string(), toward: z.string().optional() });
export const ActorRefPayload = z.object({ actor: z.string() });
export const DeathSaveResolvedPayload = z.object({
    actor: z.string(),
    total: z.number().int(),
    successes: z.number().int().min(0).max(3),
    failures: z.number().int().min(0).max(3),
});
export const DiedPayload = z.object({
    actor: z.string(),
    cause: z.string(),
    witnesses: z.array(z.string()).default([]),
    locationRef: z.string(),
});
export const KnockedOutPayload = z.object({ actor: z.string(), by: z.string() });
export const ResourceChangedPayload = z.object({
    actor: z.string(),
    resource: z.string(),
    from: z.number().int(),
    to: z.number().int(),
});
export const PropStateChangedPayload = z.object({
    prop: z.string(),
    state: PropState,
    coverAfter: Cover,
    blocksSightAfter: z.boolean(),
});
export const HazardTriggeredPayload = z.object({ prop: z.string(), actor: z.string() });
export const FledPayload = z.object({ actor: z.string(), exitId: z.string() });
/* ------------------------------------------------- encounter lifecycle */
export const HostilityDeclaredPayload = z.object({
    npc: z.string(),
    reason: z.enum(['player_act', 'npc_decision', 'faction_event', 'ambush']),
});
export const CombatStartedPayload = z.object({
    encounterId: z.string(),
    entry: z.enum(['player_hostile_act', 'npc_decision', 'encounter_table']),
    surprise: z.enum(['none', 'player_surprises', 'player_surprised', 'contested_stealth']),
});
export const GridResolvedPayload = z.object({
    encounterId: z.string(),
    spec: ResolvedSceneSpec,
    digest: z.string(),
});
export const InitiativeRolledPayload = z.object({
    order: z.array(z.object({ id: z.string(), total: z.number().int() })).min(1),
});
/**
 * How a lost fight ends (ADR-084, founder 2026-09-07): fists knock out and the loser is
 * `beaten`; killers and the deeply wronged rob; a drawn blade and a deep grudge, under a
 * tier that shows violence, execute. `none` for a fight that was not lost.
 */
export const DefeatResolution = z.enum(['none', 'spared', 'beaten', 'robbed', 'executed', 'killed']);
export const CombatEndedPayload = z.object({
    outcome: CombatOutcome,
    rounds: z.number().int().min(0),
    xpEquivalent: z.number().int().min(0),
    /** A blade or a spear was used by either side; fists alone never make a fight lethal. */
    lethal: z.boolean().default(false),
    resolution: DefeatResolution.default('none'),
    /** Who stood over the player at the end, for the narrator. */
    standing: z.array(z.string()).default([]),
});
/* -------------------------------------------------------- world payloads */
export const TimeAdvancedPayload = z.object({
    fromWorldMinutes: z.number().int(),
    toWorldMinutes: z.number().int(),
    day: z.number().int().min(1),
    period: TimeOfDay,
    weather: Weather,
});
export const SceneChangedPayload = z.object({
    fromLocationRef: z.string().optional(),
    toLocationRef: z.string(),
    moodTag: z.string().default(''),
});
/** ADR-113: the character was told of these places; the map pins them in the fog. */
export const PlacesHeardOfPayload = z.object({
    locationRefs: z.array(z.string().min(1)).min(1),
});
export const RelationshipChangedPayload = z.object({
    npc: z.string(),
    axis: RelationshipAxis,
    from: z.number().int(),
    to: z.number().int(),
    mood: NpcMood,
    provenance: z.string(),
});
export const GoldChangedPayload = z.object({
    actor: z.string(),
    deltaCp: z.number().int(),
    totalCp: z.number().int(),
    reason: z.string(),
});
export const ItemMovedPayload = z.object({
    itemInstanceId: z.string(),
    from: z.string(),
    to: z.string(),
    qty: z.number().int().min(1),
});
export const SupplyChangedPayload = z.object({
    settlement: z.string(),
    good: z.string(),
    from: z.number(),
    to: z.number(),
    cause: z.string(),
});
export const PriceChangedPayload = z.object({
    settlement: z.string(),
    good: z.string(),
    fromCp: z.number().int(),
    toCp: z.number().int(),
    /** One-line cause the ticker shows: "caravana saqueada na estrada do norte". */
    causeKey: z.string(),
});
export const NpcMovedPayload = z.object({
    npc: z.string(),
    fromLocationRef: z.string(),
    toLocationRef: z.string(),
    activity: z.string(),
});
/* ADR-110: an errand is an NPC's standing intent toward the player — news to give,
 * a debt to collect, a summons, a grievance — and the three ways they may act on it. */
export const ErrandReason = z.enum(['news', 'debt', 'summons', 'grievance', 'favour', 'warning']);
/** `chance`: only when they share a place; `watchful`: also across a square in daylight; `hunting`: they leave their day and follow what they know. */
export const ErrandSeeking = z.enum(['chance', 'watchful', 'hunting']);
export const ErrandOpenedPayload = z.object({
    errandId: z.string(),
    npc: z.string(),
    name: z.string().default(''),
    reason: ErrandReason,
    seeking: ErrandSeeking,
    brief: z.string().default(''),
    /** World minute the seeker acts from, and the minute a hunt is given up. */
    from: z.number().int().min(0).optional(),
    until: z.number().int().min(0).optional(),
});
export const ErrandClosedPayload = z.object({
    errandId: z.string(),
    npc: z.string(),
    /** `met`: they reached the player; `gave_up`: the hunt ran out; `lost`: the trail went cold; `cancelled`: the world withdrew it. */
    why: z.enum(['met', 'gave_up', 'lost', 'cancelled']),
    atLocationRef: z.string().optional(),
});
/** ADR-110: an NPC approaches the player on their own errand. A tension beat, never a fight. */
export const NpcApproachedPayload = z.object({
    errandId: z.string(),
    npc: z.string(),
    name: z.string().default(''),
    reason: ErrandReason,
    brief: z.string().default(''),
    locationRef: z.string(),
    locationLabel: z.string().default(''),
    /** `here`: they were in the same place; `seen`: they saw the player across the open and crossed to them; `found`: they went looking and found them. */
    how: z.enum(['here', 'seen', 'found']),
});
export const RumorCreatedPayload = z.object({
    rumorId: z.string(),
    category: RumorCategory,
    about: z.string(),
    claimTemplateKey: z.string(),
    hop: z.number().int().min(0),
    reliability: z.enum(['certain', 'likely', 'doubtful', 'false']),
    source: z.string(),
    day: z.number().int().min(1),
});
export const QuestStageEnteredPayload = z.object({
    questId: z.string(),
    fromStage: z.string().optional(),
    toStage: z.string(),
    evidence: z.string().max(160).default(''),
    milestone: z.boolean().default(false),
});
export const FactAssertedPayload = z.object({
    entityRef: z.string(),
    fact: z.string().max(200),
    kind: z.enum(['appearance', 'backstory', 'belief', 'world']),
    provenance: z.string(),
});
export const DisputePayload = z.object({
    entityRef: z.string(),
    fact: z.string().max(200),
    raisedBy: z.enum(['player', 'engine']),
    status: z.enum(['open', 'upheld', 'rejected']),
});
export const PromiseMadePayload = z.object({
    promiseId: z.string(),
    kind: z.enum(['threat', 'reward', 'mystery', 'reunion']),
    text: z.string().max(160),
    sessionMade: z.number().int().min(1),
    payoffBy: z.number().int().min(1),
});
export const PromisePaidPayload = z.object({
    promiseId: z.string(),
    evidence: z.string().max(160),
});
export const CoinsTransferredPayload = z.object({
    direction: z.enum(['pay', 'receive']),
    counterpart: z.string(),
    amountCp: z.number().int().min(0),
    goldCpAfter: z.number().int().min(0),
    /** A deal the purse could not cover in full: what stayed owed. */
    shortCp: z.number().int().min(0).default(0),
    reason: z.string().max(120).default(''),
});
/** ADR-103: a purchase or a sale priced from the catalogue, coin and belongings moved. */
export const ItemsTradedPayload = z.object({
    side: z.enum(['buy', 'sell']),
    counterpart: z.string(),
    itemId: z.string(),
    label: z.string(),
    qty: z.number().int().min(1),
    unitCp: z.number().int().min(0),
    totalCp: z.number().int().min(0),
    goldCpAfter: z.number().int().min(0),
});
/** ADR-129: a piece a defeated enemy left, taken into the belongings — by the engine, from a stat block's weapon. */
export const ItemsTakenPayload = z.object({
    /** The defeated combatant it was taken from. */
    from: z.string(),
    /** The catalogue item the stat block's label names, when it names one; null for a label like `ritual sickle`. */
    itemId: z.string().nullable(),
    label: z.string(),
    qty: z.number().int().min(1),
});
export const DirectorIntervenedPayload = z.object({
    kind: z.string(),
    reason: z.string(),
    /** Which route promoted this beat: storylet_fit | best_available | skeleton_forced. */
    peakSource: z.enum(['storylet_fit', 'best_available', 'skeleton_forced']).optional(),
});
export const LevelUpEventPayload = z.object({
    actor: z.string(),
    from: z.number().int().min(1),
    to: z.number().int().min(2),
    milestoneRef: z.string(),
    /** ADR-091: the hit points the levels brought, and the new maximum. */
    hpGained: z.number().int().min(0).optional(),
    hpMax: z.number().int().min(1).optional(),
});
export const PlayerDiedPayload = z.object({
    actor: z.string(),
    cause: z.string(),
    witnesses: z.array(z.string()).default([]),
    locationRef: z.string(),
    daysSurvived: z.number().int().min(0),
});
export const SuccessionChosenPayload = z.object({
    successorRef: z.string(),
    inheritedFrom: z.string(),
    kind: z.enum(['met_npc', 'newcomer']),
});
export const SessionStartedPayload = z.object({
    campaignId: z.string(),
    sessionNumber: z.number().int().min(1),
    /** sha256(sessionSeed), published in the lobby and the Ledger. */
    seedCommitment: z.string().regex(/^[0-9a-f]{64}$/),
    ruleVersion: z.string(),
    packBundleHash: z.string(),
    locale: z.enum(['en-US', 'pt-BR']),
});
export const SessionEndedPayload = z.object({
    /** The revealed `sessionSeed`; every roll is reproducible from here (§5.4). */
    seedReveal: z.string().regex(/^[0-9a-f]{64}$/),
    reason: z.enum(['player_asked', 'gm_tool', 'hard_stop', 'grace_expired', 'server_abort', 'death']),
    playedMinutes: z.number().int().min(0),
});
/* -------------------------------------------------- negotiation payloads */
export const NegotiationOpenedPayload = z.object({
    negotiationId: z.string(),
    with: z.array(z.string()).min(1),
    stake: z.string().max(160),
    stance: z.enum(['hostile', 'guarded', 'open', 'invested']),
    patience: z.number().int().min(3).max(6),
});
export const LeverPlayedPayload = z.object({
    negotiationId: z.string(),
    leverId: z.string(),
    by: z.enum(['player', 'npc']),
    kind: z.enum(['fact', 'debt', 'threat', 'good']),
    effect: z.string().max(160),
});
export const StanceChangedPayload = z.object({
    negotiationId: z.string(),
    from: z.enum(['hostile', 'guarded', 'open', 'invested']),
    to: z.enum(['hostile', 'guarded', 'open', 'invested']),
    cause: z.string().max(120),
});
export const ConcessionMadePayload = z.object({
    negotiationId: z.string(),
    by: z.enum(['player', 'npc']),
    slot: NegotiationSlot,
    value: z.number().int(),
});
export const PatienceSpentPayload = z.object({
    negotiationId: z.string(),
    remaining: z.number().int().min(0),
});
/**
 * ADR-077. Logged **before** `RollRequested`, so the assessment cannot be steered
 * by knowing the die, and so the dice log can show what the words were worth.
 */
export const ArgumentAssessedPayload = z.object({
    negotiationId: z.string(),
    skill: z.enum(['persuasion', 'intimidation', 'deception', 'performance']),
    modifier: z.number().int().min(-2).max(3),
    rationale: z.string().max(160),
    dc: z.number().int(),
});
export const NegotiationClosedPayload = z.object({
    negotiationId: z.string(),
    outcome: z.enum(['deal', 'walkout', 'impasse', 'escalation']),
    terms: z.array(z.object({ slot: NegotiationSlot, value: z.number().int() })).default([]),
});
/* ------------------------------------------------ safety and ops payloads */
export const PiBlockedPayload = z.object({
    stem: z.string(),
    replacement: z.string(),
    surface: z.enum(['narration', 'reaction_line', 'guia', 'engine_line', 'assert_fact', 'batch']),
});
export const AgeGuardTrippedPayload = z.object({
    npc: z.string(),
    ageBand: z.string(),
    rejectedTool: z.string(),
});
/**
 * The X-card (ADR-063 §5). The tabletop safety tool as an engine rule: the player
 * plays it, the current turn's outcome goes off-screen, and the session's content
 * tier narrows to its safe end for the rest of the session.
 *
 * It is logged, alongside `AgeGuardTripped`, because a safety tool that leaves no
 * trace cannot be audited — not so the moment can be reviewed for its content,
 * which is why nothing here records *what* was happening. `source` is how the card
 * was played, `turnId` is which turn it interrupted, and `tierAfter` is the
 * narrowing it caused, which is the whole of its mechanical effect.
 */
/** A line drawn (ADR-063 §5): which tags, and how many the session now carries. */
/** ADR-087: what the game master prepared for tonight — offers, never a script. */
/** ADR-088: a journey the engine granted end to end; the road was quiet. */
export const JourneyCompletedPayload = z.object({
    fromLocationRef: z.string(),
    toLocationRef: z.string(),
    toLocationLabel: z.string().default(''),
    minutes: z.number().int().min(0),
    through: z.array(z.string()),
});
/** ADR-088: a journey the engine stopped on the road — a hazard, or nightfall with legs still to walk. */
export const JourneyInterruptedPayload = z.object({
    fromLocationRef: z.string(),
    toLocationRef: z.string(),
    atLocationRef: z.string(),
    atLocationLabel: z.string().default(''),
    toLocationLabel: z.string().default(''),
    minutes: z.number().int().min(0),
    /** A hazard kind from the road's table ('wolves', 'bandits', …), or 'nightfall'. */
    hazard: z.string(),
    period: z.string(),
});
/** ADR-088 §5: the night in camp passed; the character rested. */
export const CampPassedPayload = z.object({
    locationRef: z.string(),
    minutes: z.number().int().min(0),
    watches: z.number().int().min(1),
    hpAfter: z.number().int(),
    /** ADR-092: whether the sleeper got the SRD's six hours (four in a trance) — a long rest — or only a short one. */
    rested: z.boolean().optional(),
    /** ADR-092: watches nobody stood; a hazard in one arrives unannounced. */
    unwatched: z.number().int().min(0).optional(),
});
/** ADR-088 §5: a watch was broken; whether the watcher saw it coming is the surprise. */
export const CampInterruptedPayload = z.object({
    locationRef: z.string(),
    watch: z.number().int().min(1),
    minutes: z.number().int().min(0),
    hazard: z.string(),
    /** Who had the watch: `player` or an NPC ref. */
    watcher: z.string().default('player'),
    passivePerception: z.number().int(),
    dc: z.number().int(),
    surprised: z.boolean(),
});
export const SessionPlannedPayload = z.object({
    /** Who wrote the sheet: the engine's deterministic prep, or the chosen persona over it (ADR-087 §4). */
    sheetBy: z.enum(['engine', 'persona']).default('engine'),
    lengthMinutes: z.number().int().min(10),
    kickstarters: z.array(z.string()).default([]),
    peaks: z.array(z.string()).default([]),
    landing: z.string().nullable().default(null),
    sheet: z.string().max(1200).default(''),
});
export const SafetyLineDrawnPayload = z.object({
    tags: z.array(z.string()).min(1),
    totalTags: z.number().int().min(1),
});
export const SafetyCardPlayedPayload = z.object({
    turnId: z.string(),
    source: z.enum(['voice', 'button', 'text']),
    tierAfter: z.object({
        violenceCompletion: z.enum(['threat', 'shown']),
        deathNarration: z.enum(['offscreen', 'onscreen']),
    }),
});
export const ToneCorrectedPayload = z.object({
    checkSeq: z.number().int().nonnegative(),
    tag: ToneTag,
    ruleId: z.string(),
});
export const CoupDeGraceAllowedPayload = z.object({
    attacker: z.string(),
    target: z.string(),
    reason: z.string().max(160),
});
export const WatermarkTimeoutPayload = z.object({
    heldSeq: z.number().int().nonnegative(),
    heldMs: z.number().int().min(0),
});
export const VadDegradedPayload = z.object({
    reason: z.enum(['executor_overrun', 'model_error']),
    missedFrames: z.number().int().min(0),
});
export const SessionAbortedPayload = z.object({
    cause: z.enum(['server', 'provider', 'voice_and_text_down']),
    detail: z.string().max(200),
});
/**
 * One step of the per-session cost governor (§8.8): "every step is a `GovernorStage`
 * event". Stage 0 is the undegraded state; 1 turns reaction lines off, 2 moves the
 * Guia to text, 3 caps narration length, 4 forces a fold and stops continuations.
 *
 * `skippedStage1` records the latency precondition firing: a session whose TTFT EWMA
 * is running hot goes to stage 3 first rather than buying $0.01 out of its own
 * latency cover, and the reason that happened has to be in the log, not inferred
 * from a gap in the stage sequence.
 */
export const GovernorStagePayload = z.object({
    stage: z.number().int().min(0).max(4),
    trigger: z.enum(['projected_cogs', 'minutes_left', 'stage1_skipped']),
    /** USD, the governor's `spent_so_far + turns_left × EWMA(turn_cost)`. */
    projectedCogs: z.number().min(0),
    /** The session's rolling time-to-first-token EWMA at the decision. */
    ttftEwmaMs: z.number().int().min(0),
    skippedStage1: z.boolean(),
});
/**
 * Fleet-level admission control (§8.8), in the pool manager rather than the actor:
 * "Both thresholds are config, both are logged as `AdmissionThrottled`."
 *
 * Above 85% of tier (or a 2% rolling 429 rate) new sessions stop being placed;
 * above 95% they are first placed on the Balanced profile, which is what
 * `placedOnBalanced` distinguishes from an outright refusal. Sessions already in
 * flight are never degraded by this path.
 */
export const AdmissionThrottledPayload = z.object({
    reason: z.enum(['rate_429', 'tier_rpm']),
    /** The observed value: a 0..1 429 rate, or measured RPM. */
    measured: z.number().min(0),
    /** The configured threshold it crossed, in the same units as `measured`. */
    threshold: z.number().min(0),
    placedOnBalanced: z.boolean(),
});
export const LightingChangedPayload = z.object({ lighting: Lighting });
/* ------------------------------------------------------------- the union */
export const EverwakeEvent = z.discriminatedUnion('kind', [
    // Rolls
    defineEvent('RollRequested', RollRequestedPayload),
    defineEvent('RollResolved', RollResolvedPayload),
    // Combat resolution
    defineEvent('AttackResolved', AttackResolvedPayload),
    defineEvent('DamageApplied', DamageAppliedPayload),
    defineEvent('Healed', HealedPayload),
    defineEvent('SaveResolved', SaveResolvedPayload),
    defineEvent('CheckResolved', CheckResolvedPayload),
    defineEvent('ConcentrationChecked', ConcentrationCheckedPayload),
    defineEvent('SpellCast', SpellCastPayload),
    defineEvent('SpellEnded', SpellEndedPayload),
    defineEvent('ConditionEnded', ConditionEndedPayload),
    defineEvent('ConditionApplied', ConditionChangedPayload),
    defineEvent('ConditionRemoved', ConditionChangedPayload),
    defineEvent('EffectStarted', EffectStartedPayload),
    defineEvent('EffectEnded', EffectEndedPayload),
    defineEvent('Moved', MovedPayload),
    defineEvent('Dashed', DashedPayload),
    defineEvent('DroppedToZero', ActorRefPayload),
    defineEvent('DeathSaveResolved', DeathSaveResolvedPayload),
    defineEvent('Stabilized', ActorRefPayload),
    defineEvent('Died', DiedPayload),
    defineEvent('KnockedOut', KnockedOutPayload),
    defineEvent('ResourceChanged', ResourceChangedPayload),
    defineEvent('PropStateChanged', PropStateChangedPayload),
    defineEvent('HazardTriggered', HazardTriggeredPayload),
    defineEvent('SurrenderOffered', ActorRefPayload),
    defineEvent('Fled', FledPayload),
    // Encounter lifecycle
    defineEvent('HostilityDeclared', HostilityDeclaredPayload),
    defineEvent('CombatStarted', CombatStartedPayload),
    defineEvent('GridResolved', GridResolvedPayload),
    defineEvent('InitiativeRolled', InitiativeRolledPayload),
    defineEvent('RoundStarted', z.object({ round: z.number().int().min(1) })),
    defineEvent('TurnStarted', ActorRefPayload),
    defineEvent('TurnEnded', ActorRefPayload),
    defineEvent('CombatEnded', CombatEndedPayload),
    // World
    defineEvent('TimeAdvanced', TimeAdvancedPayload),
    defineEvent('SceneChanged', SceneChangedPayload),
    defineEvent('PlacesHeardOf', PlacesHeardOfPayload),
    defineEvent('RelationshipChanged', RelationshipChangedPayload),
    defineEvent('GoldChanged', GoldChangedPayload),
    defineEvent('ItemMoved', ItemMovedPayload),
    defineEvent('SupplyChanged', SupplyChangedPayload),
    defineEvent('PriceChanged', PriceChangedPayload),
    defineEvent('NpcMoved', NpcMovedPayload),
    defineEvent('RumorCreated', RumorCreatedPayload),
    defineEvent('QuestStageEntered', QuestStageEnteredPayload),
    defineEvent('FactAsserted', FactAssertedPayload),
    defineEvent('Dispute', DisputePayload),
    defineEvent('PromiseMade', PromiseMadePayload),
    defineEvent('PromisePaid', PromisePaidPayload),
    defineEvent('CoinsTransferred', CoinsTransferredPayload),
    defineEvent('ItemsTraded', ItemsTradedPayload),
    defineEvent('ItemsTaken', ItemsTakenPayload),
    defineEvent('SessionPlanned', SessionPlannedPayload),
    defineEvent('JourneyCompleted', JourneyCompletedPayload),
    defineEvent('JourneyInterrupted', JourneyInterruptedPayload),
    defineEvent('CampPassed', CampPassedPayload),
    defineEvent('CampInterrupted', CampInterruptedPayload),
    defineEvent('ErrandOpened', ErrandOpenedPayload),
    defineEvent('ErrandClosed', ErrandClosedPayload),
    defineEvent('NpcApproached', NpcApproachedPayload),
    defineEvent('DirectorIntervened', DirectorIntervenedPayload),
    defineEvent('LevelUp', LevelUpEventPayload),
    defineEvent('PlayerDied', PlayerDiedPayload),
    defineEvent('SuccessionChosen', SuccessionChosenPayload),
    defineEvent('SessionStarted', SessionStartedPayload),
    defineEvent('SessionEnded', SessionEndedPayload),
    defineEvent('LightingChanged', LightingChangedPayload),
    // Negotiation peak machine (§6.10)
    defineEvent('NegotiationOpened', NegotiationOpenedPayload),
    defineEvent('LeverPlayed', LeverPlayedPayload),
    defineEvent('StanceChanged', StanceChangedPayload),
    defineEvent('ConcessionMade', ConcessionMadePayload),
    defineEvent('PatienceSpent', PatienceSpentPayload),
    defineEvent('NegotiationClosed', NegotiationClosedPayload),
    defineEvent('ArgumentAssessed', ArgumentAssessedPayload),
    // Safety, governor and ops
    defineEvent('PIBlocked', PiBlockedPayload),
    defineEvent('AgeGuardTripped', AgeGuardTrippedPayload),
    defineEvent('SafetyCardPlayed', SafetyCardPlayedPayload),
    defineEvent('SafetyLineDrawn', SafetyLineDrawnPayload),
    defineEvent('ToneCorrected', ToneCorrectedPayload),
    defineEvent('CoupDeGraceAllowed', CoupDeGraceAllowedPayload),
    defineEvent('WatermarkTimeout', WatermarkTimeoutPayload),
    defineEvent('VadDegraded', VadDegradedPayload),
    defineEvent('SessionAborted', SessionAbortedPayload),
    defineEvent('GovernorStage', GovernorStagePayload),
    defineEvent('AdmissionThrottled', AdmissionThrottledPayload),
]);
export const EVENT_KINDS = EverwakeEvent.options.map((o) => o.shape.kind.value);
export const EventKind = z.enum(EVENT_KINDS);
/** Default ledger importance by event kind; the sifter reads it (§3.6, §4.6). */
export const EVENT_IMPORTANCE = Object.freeze({
    PlayerDied: 10,
    /* Ranked with a death: a sifted digest that dropped the moment a player drew a
     * line would let the next session walk back into it. */
    SafetyCardPlayed: 10,
    SafetyLineDrawn: 10,
    Died: 9,
    CombatStarted: 8,
    CombatEnded: 8,
    LevelUp: 8,
    QuestStageEntered: 7,
    NegotiationClosed: 6,
    ArgumentAssessed: 5,
    PromiseMade: 5,
    PromisePaid: 6,
    CoinsTransferred: 5,
    ItemsTaken: 5,
    SessionPlanned: 6,
    SpellCast: 4,
    SpellEnded: 2,
    JourneyCompleted: 6,
    JourneyInterrupted: 8,
    CampPassed: 5,
    CampInterrupted: 8,
    NpcApproached: 7,
    ErrandOpened: 4,
    ErrandClosed: 3,
    RelationshipChanged: 4,
    RumorCreated: 3,
    ItemMoved: 2,
    GoldChanged: 2,
    NpcMoved: 1,
    TimeAdvanced: 1,
});
//# sourceMappingURL=events.js.map